import { useCallback, useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  LeadConversationFieldsFragment,
  useInviteLeadToFormMutation,
  useKommoLeadThreadListQuery,
  usePostGmailReplyMutation,
  useWriteLeadEmailMutation,
} from 'core/graphql';
import { ToastTypes } from 'types';

import { appendThreadMessage } from '../Communications/threadCache';
import { IRichEmailPayload } from '../RichMail/useRichEmailDraft';
import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { leadCardTargets, useQuietRefetch } from './leadRefetch';
import {
  asClause,
  ILeadError,
  useLeadErrorText,
  useLeadsNotify,
} from './leadsFeedback';
import { leadLadderWarningKey, leadSmsReasonKey } from './leadsModel';
import { leadCrashError, toLeadErrors } from './useLeadLadders';
import { useLeadPermissions } from './useLeadPermissions';

/**
 * ТЗ 0084, этап 3 — письма лиду из карточки: треды Gmail с лидом, новое
 * письмо со своей рабочей почты, ответ в тред и «Пригласить к анкете».
 *
 * Лента треда — тот же `ConversationThreadPage`, что у договора и
 * коммуникатора (`useContractThread`): одна запись кэша на тред.
 */

export type ILeadConversation = LeadConversationFieldsFragment;

const EMPTY_CONVERSATIONS: ILeadConversation[] = [];

/** Ответ лида приходит синхронизацией Gmail — список раз в минуту. */
export const LEAD_MAIL_POLL_MS = 60000;

export interface IUseLeadConversations {
  conversations: ILeadConversation[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useLeadConversations = (
  leadId: string | null,
  enabled = true,
): IUseLeadConversations => {
  const { canUse } = useLeadPermissions();
  const skip = !canUse || !enabled || !leadId;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useKommoLeadThreadListQuery({
    variables: { leadId: leadId || '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  usePageVisiblePoll(startPolling, stopPolling, LEAD_MAIL_POLL_MS, !skip);
  const reload = useCallback(() => {
    if (!skip) refetch()?.catch(() => undefined);
  }, [refetch, skip]);
  return {
    conversations: data?.kommoLeadConversations ?? EMPTY_CONVERSATIONS,
    loading,
    error,
    refetch: reload,
  };
};

export interface ILeadMailResult {
  ok: boolean;
  errors: ILeadError[];
  conversationId: string | null;
}

export interface ILeadEmailInput {
  mailboxEmail: string;
  subject: string;
  /** ТЗ 0097: HTML письма (подстановки заполнит сервер) и вложения. */
  html: string;
  attachmentIds: string[];
  templateId?: string | null;
  requestKey: string;
}

export interface ILeadInviteInput extends ILeadEmailInput {
  startLadder: boolean;
  days: number | null;
}

export interface ILeadInviteResult extends ILeadMailResult {
  /** Почему лесенка сейчас не шлёт — письмо ушло всё равно. */
  ladderWarning: string | null;
}

/**
 * Письмо, ответ и приглашение. Ошибки полей (`NOT_YOUR_MAILBOX`,
 * `REQUIRED`, `UNKNOWN_PLACEHOLDER`…) возвращаются форме — она рисует их у
 * поля; успех — тостом. После УДАЧНОГО письма перечитываются треды, карточка
 * и журнал; после приглашения — ещё лесенки и SMS (первое SMS уходит сразу).
 * Перечитывание идёт следом и своих ошибок не показывает: исход письма —
 * ответ мутации, а не пяти запросов после неё (перепроверка 0084).
 */
export const useLeadMailActions = (leadId: string) => {
  const { t } = useTranslation();
  const notify = useLeadsNotify();
  const errorText = useLeadErrorText();
  const refetch = useQuietRefetch();
  const [writeMut, { loading: writing }] = useWriteLeadEmailMutation();
  const [inviteMut, { loading: inviting }] = useInviteLeadToFormMutation();
  const [replyMut, { loading: replying }] = usePostGmailReplyMutation();

  const afterMail = useMemo(
    () => leadCardTargets(leadId, ['threads', 'card', 'events']),
    [leadId],
  );
  const afterInvite = useMemo(
    () =>
      leadCardTargets(leadId, ['threads', 'card', 'events', 'ladders', 'sms']),
    [leadId],
  );

  const crash = useCallback(
    (caught: unknown): ILeadMailResult => {
      const error = leadCrashError(caught);
      notify(ToastTypes.error, t('leads.mail.toast.failed'), errorText(error));
      return { ok: false, errors: [], conversationId: null };
    },
    [errorText, notify, t],
  );

  const sendEmail = useCallback(
    async (input: ILeadEmailInput): Promise<ILeadMailResult> => {
      try {
        const { data } = await writeMut({
          variables: {
            leadId,
            mailboxEmail: input.mailboxEmail,
            subject: input.subject,
            html: input.html,
            attachmentIds: input.attachmentIds,
            templateId: input.templateId || null,
            requestKey: input.requestKey,
          },
        });
        const payload = data?.sendLeadEmail;
        const errors = toLeadErrors(payload?.customErrors);
        const ok = Boolean(payload?.success) && errors.length === 0;
        if (ok) {
          notify(ToastTypes.success, t('leads.mail.toast.sent'));
          refetch(afterMail);
        }
        return { ok, errors, conversationId: payload?.conversationId ?? null };
      } catch (caught) {
        return crash(caught);
      }
    },
    [afterMail, crash, leadId, notify, refetch, t, writeMut],
  );

  const invite = useCallback(
    async (input: ILeadInviteInput): Promise<ILeadInviteResult> => {
      try {
        const { data } = await inviteMut({
          variables: {
            leadId,
            mailboxEmail: input.mailboxEmail,
            subject: input.subject,
            html: input.html,
            attachmentIds: input.attachmentIds,
            templateId: input.templateId || null,
            startLadder: input.startLadder,
            days: input.startLadder ? input.days : null,
            requestKey: input.requestKey,
          },
        });
        const payload = data?.inviteLeadToFundingForm;
        const errors = toLeadErrors(payload?.customErrors);
        const ok = Boolean(payload?.success) && errors.length === 0;
        const ladderWarning = payload?.ladderWarning ?? null;
        if (ok) {
          refetch(afterInvite);
          // Те же слова, что у лесенки в карточке (`leads.smsReasons`).
          const key =
            leadSmsReasonKey(ladderWarning) ||
            leadLadderWarningKey(ladderWarning);
          notify(
            ladderWarning ? ToastTypes.warning : ToastTypes.success,
            t(
              // Лесенка не встала (`ladder_not_started`) — «лид на лесенке»
              // было бы неправдой: письмо ушло, лесенку запускают из карточки.
              input.startLadder && ladderWarning !== 'ladder_not_started'
                ? 'leads.invite.toast.sentWithLadder'
                : 'leads.invite.toast.sent',
            ),
            ladderWarning
              ? t('leads.invite.toast.ladderWarning', {
                  reason: asClause(key ? t(key) : ladderWarning),
                })
              : undefined,
          );
        }
        return {
          ok,
          errors,
          conversationId: payload?.conversationId ?? null,
          ladderWarning,
        };
      } catch (caught) {
        return { ...crash(caught), ladderWarning: null };
      }
    },
    [afterInvite, crash, inviteMut, leadId, notify, refetch, t],
  );

  /**
   * Ответ в тред. Тред НЕ перечитываем: `appendThreadMessage` дописывает
   * сообщение в кэш сам (рефетч первой страницы схлопнул бы догруженные
   * ранние — ТЗ 0033); список тредов перечитывается (`lastMessageAt`).
   */
  const reply = useCallback(
    async (
      conversationId: string,
      body: IRichEmailPayload,
      /** Перепроверка F35: самое новое показанное входящее треда. */
      seenMessageId?: string | null,
    ): Promise<ILeadMailResult> => {
      try {
        const { data } = await replyMut({
          variables: {
            conversationId,
            html: body.html,
            attachmentIds: body.attachmentIds,
            seenMessageId: seenMessageId || null,
          },
          update: (cache, res) => {
            const message = res.data?.sendGmailReply?.message;
            if (message?.id) {
              appendThreadMessage(
                cache,
                conversationId,
                message as { id: string } & Record<string, unknown>,
              );
            }
          },
        });
        const payload = data?.sendGmailReply;
        const errors = toLeadErrors(payload?.customErrors);
        const ok = Boolean(payload?.success) && errors.length === 0;
        if (ok) {
          notify(ToastTypes.success, t('leads.mail.toast.replied'));
          refetch(afterMail);
        }
        return { ok, errors, conversationId };
      } catch (caught) {
        return crash(caught);
      }
    },
    [afterMail, crash, notify, refetch, replyMut, t],
  );

  return {
    sendEmail,
    invite,
    reply,
    busy: { writing, inviting, replying },
  };
};
